import React, { useCallback, useEffect, useState } from "react";
import { Dialog, Typography, LinearProgress, Button } from "@material-ui/core";
import cx from "classnames";
import ClockIcon from "./ClockIcon";

import classes from "./SessionTimeout.module.scss";

// setInterval reference for setting/clearing inactivity timer
let inactivityInterval: any;

const INACTIVITY_THRESHOLD = 60;

interface SessionTimeoutProps {
    onLogoutUser: (source:string) => void;
    inactivityTimeout: number;
    documentTitle: string;
    classname?: string;
}

const SessionTimeout: React.FC<SessionTimeoutProps> = ({
    onLogoutUser,
    inactivityTimeout,
    documentTitle,
    classname,
}) => {
  const [remainingInactivity, setRemainingInactivity] = useState<number>(inactivityTimeout);
  const [open, setOpen] = useState<boolean>(inactivityTimeout <= INACTIVITY_THRESHOLD);

  useEffect(() => {
    setRemainingInactivity(inactivityTimeout);
    setOpen(inactivityTimeout <= INACTIVITY_THRESHOLD);
  }, [inactivityTimeout])

  // increment current inactivity counter
  const decrementRemainingInactivity = () => {
    setRemainingInactivity((p) => p - 1);
  };
  // handle extend session action
  const handleExtendSession = () => {
    setOpen(false);
    document.title = documentTitle;
    setTimeout(() => setRemainingInactivity(inactivityTimeout), 250);
  };
  const handleLogoutButtonClick = () => {
    onLogoutUser("logoutButtonClicked");
  };
  // function to manually trigger inactivity timeout warning dialog
  window.inactivityTimeoutWarning = () => {
    setRemainingInactivity(INACTIVITY_THRESHOLD + 1);
  };

  // callback to reset the inactivity timer on user interaction
  window.handleUserInteraction = useCallback(() => {
    // if warning dialog is open, do not reset on user interaction
    if (open || remainingInactivity <= INACTIVITY_THRESHOLD) return;
    setRemainingInactivity(inactivityTimeout);
  }, [open, inactivityTimeout, remainingInactivity]);

  // once we have the user's inactivity timeout duration, start the inactivity timer
  useEffect(() => {
    if (inactivityTimeout && !inactivityInterval) {
      setRemainingInactivity(inactivityTimeout);
      inactivityInterval = setInterval(decrementRemainingInactivity, 1000);
    }
  }, [inactivityTimeout]);

  // start listening for user activity to reset the timer on mount
  // clean up inactivity interval and interaction listeners on unmount
  useEffect(() => {
    const cb = () => window.handleUserInteraction();
    document.addEventListener("keypress", cb);
    document.addEventListener("click", cb);
    document.addEventListener("scroll", cb, true);
    return () => {
      clearInterval(inactivityInterval);
      document.removeEventListener("keypress", cb);
      document.removeEventListener("click", cb);
      document.removeEventListener("scroll", cb, true);
    };
  }, []);

  // monitor remaining inactivity
  useEffect(() => {
    // when the 60s remaining threshold is hit, open the warning dialog
    if (!open && remainingInactivity === 60) {
      setOpen(true);
      return;
    }
    // if the warning is open and the counter hits 0, log the user out
    if (open) {
      if (remainingInactivity <= 0) {
        clearInterval(inactivityInterval);
        onLogoutUser("inactivityTimeout");
        return;
      }

      if (remainingInactivity <= INACTIVITY_THRESHOLD) {
        document.title = !!(remainingInactivity % 2)
          ? "Timeout Warning"
          : documentTitle;
      }
    }
  }, [open, remainingInactivity]);

  return (
    <Dialog className={cx(classes.sessionTimeoutDialog, classname)} open={open}>
      <div className={classes.content}>
        <ClockIcon />
        <Typography className={classes.header} variant="h5">
          Inactivity Warning!
        </Typography>
        <Typography className={classes.message} variant="body1">
          You will be logged out of Suralink shortly due to inactivity. If you
          are still working, you may extend your session.
        </Typography>
        <Typography className={classes.countdownLabel} variant="body2">
          {`${remainingInactivity} seconds`}
        </Typography>
        <LinearProgress
          className={classes.countdownProgress}
          variant="determinate"
          value={Math.floor((remainingInactivity / INACTIVITY_THRESHOLD) * 100)}
        />
        <div className={classes.buttons}>
          <Button
            className={cx(classes.button, classes.extend)}
            variant="outlined"
            color="primary"
            onClick={handleExtendSession}
          >
            Extend Session
          </Button>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            onClick={handleLogoutButtonClick}
          >
            Logout
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default SessionTimeout;
